/**
 * Dashboard Service
 * Handles dashboard statistics and summary data
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { DASHBOARD_ENDPOINTS } from '../endpoints';

/**
 * @typedef {Object} DashboardSummary
 * @property {number} totalForms - Total number of forms
 * @property {number} activeForms - Number of active forms
 * @property {number} totalSubmissions - Total submissions across all forms
 * @property {number} pendingSubmissions - Number of pending submissions
 * @property {number} activeUsers - Number of active users
 * @property {number} formsThisMonth - Number of forms created this month
 * @property {Array} recentForms - Recent forms data
 * @property {Array} recentSubmissions - Recent submissions data
 */

const dashboardService = {
  /**
   * Get dashboard summary statistics
   * @returns {Promise<DashboardSummary>} Dashboard summary data
   */
  getDashboardSummary: async () => {
    return apiRequest(() =>
      axiosInstance.get(DASHBOARD_ENDPOINTS.SUMMARY)
    );
  },

  /**
   * Get submission analytics with monthly trends
   * @param {number} months - Number of months to include (default: 12)
   * @returns {Promise<Object>} Submission analytics data
   */
  getAnalytics: async (months = 12) => {
    return apiRequest(() =>
      axiosInstance.get(`${DASHBOARD_ENDPOINTS.ANALYTICS}?months=${months}`)
    );
  },

  /**
   * Get upcoming work orders for the next N days
   * @param {number} days - Number of days to look ahead (default: 14)
   * @returns {Promise<Array>} List of upcoming work orders
   */
  getUpcomingWorkOrders: async (days = 14) => {
    return apiRequest(() =>
      axiosInstance.get(`${DASHBOARD_ENDPOINTS.UPCOMING_WORK_ORDERS}?days=${days}`)
    );
  },

  /**
   * Helper: Format dashboard statistics for display
   * @param {DashboardSummary} summary - Dashboard summary data
   * @returns {Array} Formatted stats array
   */
  formatDashboardStats: (summary) => {
    if (!summary) return [];

    return [
      {
        icon: 'clipboard-list',
        label: 'Total Forms',
        value: summary.totalForms || 0,
        color: 'tint'
      },
      {
        icon: 'bar-chart-3',
        label: 'Form Responses',
        value: summary.totalSubmissions || 0,
        color: 'success'
      },
      {
        icon: 'users',
        label: 'Active Users',
        value: summary.activeUsers || 0,
        color: 'warning'
      },
      {
        icon: 'zap',
        label: 'Forms This Month',
        value: summary.formsThisMonth || 0,
        color: 'info'
      }
    ];
  },
};

export default dashboardService;
